/* ============================================================== */
/*                                                                */
/*   DocumentV4 · sequence ของหน้า "Document" (HIS V4)            */
/*   (แบบฟอร์มคลินิก · เอกสารสแกน · ใบรับรองแพทย์)                */
/*                                                                */
/*   practice เดียวกับ ExamV4 / PatientOrdersV4 / ResultSummaryV4: */
/*     data    -> state.DocumentV4Sequence                        */
/*     loading -> state.buttonLoadCheck["CardDocumentV4_<ACTION>"]*/
/*     error   -> state.errorMessage["CardDocumentV4"]            */
/*     card เรียก props.runSequence({ sequence, action, card, ... })*/
/*                                                                */
/*   HIS V3 ที่อ้างอิง:                                           */
/*     react-lib/apps/common/CardPrintList.tsx                    */
/*       GET /apis/core/document-type/?printable=true            */
/*     react-lib/apps/HISV3/common/CardScannedDocument.tsx        */
/*       GET/POST /apis/core/scanned-document/                    */
/*       PATCH /apis/core/scanned-document/${pk}/                 */
/*     react-lib/apps/HISV3/DPO/sequence/DoctorCertificate.ts     */
/*       GET /apis/DPO/doctor-certificates-type/                  */
/*       GET /apis/PTM/form-data-action-log-all/                  */
/*       POST/PUT /apis/PTM/form-data/                            */
/*                                                                */
/* ============================================================== */

import DocumentCategoryList from "issara-sdk/apis/DocumentCategoryList_core";
import DocumentTypeList from "issara-sdk/apis/DocumentTypeList_core";
import ScannedDocumentList from "issara-sdk/apis/ScannedDocumentList_core";
import ScannedDocumentDetail from "issara-sdk/apis/ScannedDocumentDetail_core";
import DoctorCertificatesTypeList from "issara-sdk/apis/DoctorCertificatesTypeList_apps_DPO";
import DoctorDetail from "issara-sdk/apis/DoctorDetail_core";
import FormDataActionLogAllList from "issara-sdk/apis/FormDataActionLogAllList_apps_PTM";
import FormDataDetail from "issara-sdk/apis/FormDataDetail_apps_PTM";
import FormDataList from "issara-sdk/apis/FormDataList_apps_PTM";
import PatientEMRList from "issara-sdk/apis/PatientEMRList_apps_DPO";
import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";
import EncounterMedicalRecordList from "issara-sdk/apis/EncounterMedicalRecordList_core";
import DiagnosisMedicalRecordDetail from "issara-sdk/apis/DiagnosisMedicalRecordDetail_apps_DPO";

import {
  BLC_STATUS,
  generatePrefixedActions,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  apiErrorTextOf,
  errorTextOf,
  markButtonLoadCheck,
  seqBtnKey,
  seqErrorKey,
  writeSequenceState,
  type BLC_STATUS_TYPE,
  type SeqMeta,
} from "./sequenceStateV4";

import {
  asNumber,
  diagnosisTextFromApi,
  documentCategoriesFromApi,
  documentTypesFromApi,
  doctorForCertFromApi,
  emptyDocumentV4Slice,
  examAbsenceCertData,
  formsFromApi,
  medCertCreatePayload,
  medCertReprintPayload,
  medCertsFromLogs,
  medCertTypesFromApi,
  newDocumentCode,
  normalizeDocumentV4Slice,
  scannedDocPagePayload,
  scannedDocsFromApi,
  type DiagnosisStatus,
  type DocumentActionResult,
  type DocumentRunParams,
  type DocumentV4Slice,
  type MedCertDetail,
  type ScanPage,
} from "../../patient/documentModelV4";

/* ============================================================== */
/*   SECTION 1 · TYPE-DEFINING CONSTANTS                           */
/* ============================================================== */

export const SEQUENCE_NAME = "DocumentV4" as const;
export const CARD_NAME = "CardDocumentV4";

export const ACTIONS = {
  LOAD: "LOAD",
  LOAD_FORMS: "LOAD_FORMS",
  LOAD_SCANNED: "LOAD_SCANNED",
  SAVE_SCANNED_DOC: "SAVE_SCANNED_DOC",
  DELETE_SCANNED_DOC: "DELETE_SCANNED_DOC",
  LOAD_MED_CERTS: "LOAD_MED_CERTS",
  ISSUE_MED_CERT: "ISSUE_MED_CERT",
  GET_MED_CERT: "GET_MED_CERT",
  REPRINT_MED_CERT: "REPRINT_MED_CERT",
} as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const DOCUMENT_SLICE_KEY = "DocumentV4Sequence";

export type State = {
  DocumentV4Sequence: Partial<DocumentV4Slice> | null;
};

export const StateInitial: State = { DocumentV4Sequence: null };

type Controller = {
  getState: () => Record<string, any>;
  setState: (state: any, callback?: () => void) => any;
  apiToken?: string;
  data?: Record<string, any>;
  dispatch?: (action: any) => void;
};

type ApiTuple = [unknown, unknown, unknown];

const errorMessageOf = (result: ApiTuple): string | null =>
  apiErrorTextOf(result, { fallbackText: "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้" });

/** division/device ที่ SDK ต้องการในทุกคำขอ scan_division ของ BE อ่านจากค่านี้ */
const extraOf = (controller: Controller) => ({
  division: controller.data?.division,
  device: controller.data?.device,
});

const sliceOf = (controller: Controller): DocumentV4Slice =>
  normalizeDocumentV4Slice(controller.getState()[DOCUMENT_SLICE_KEY]);

type SliceMeta = { card?: string; action?: string };

const metaOf = (meta: SliceMeta): SeqMeta => ({
  sequence: SEQUENCE_NAME,
  card: meta.card ?? CARD_NAME,
  action: meta.action,
});

type WritePatch = Partial<DocumentV4Slice> & {
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
};

const writeSlice = (controller: Controller, meta: SliceMeta, patch: WritePatch): any =>
  writeSequenceState<DocumentV4Slice>(
    controller,
    metaOf(meta),
    DOCUMENT_SLICE_KEY,
    sliceOf(controller),
    { ...patch, sequenceIndex: "Action" },
    emptyDocumentV4Slice,
  );

const setBtn = (controller: Controller, metas: SliceMeta[], status: BLC_STATUS_TYPE): void =>
  markButtonLoadCheck(controller, metas.map(metaOf), status);

const reportError = (controller: Controller, meta: SliceMeta, message: string): void =>
  writeSlice(controller, meta, { error: message });

/** บันทึกผลของ action เขียนข้อมูล ให้ card ตัดสินจากผลจริงผ่าน requestId */
const writeActionResult = (
  controller: Controller,
  meta: SliceMeta,
  params: DocumentRunParams,
  result: Omit<DocumentActionResult, "requestId" | "action">,
  error?: string | null,
): void => {
  writeSlice(controller, meta, {
    actionResult: { requestId: String(params.requestId ?? ""), action: String(meta.action), ...result },
    ...(error === undefined ? {} : { error }),
  });
};

const failAction = (
  controller: Controller,
  meta: SliceMeta,
  params: DocumentRunParams,
  message: string,
): void => writeActionResult(controller, meta, params, { ok: false }, message);

/** ล้าง error เดิมแล้วตั้ง loading ของ action (writeSlice error:null ตั้งปุ่มเป็น SUCCESS จึงต้องทำก่อน) */
const beginAction = (controller: Controller, meta: SliceMeta): void => {
  writeSlice(controller, meta, { error: null });
  setBtn(controller, [meta], BLC_STATUS.LOADING);
};

type Target = { patientId?: number; encounterId?: number };

const targetOf = (slice: DocumentV4Slice): Target => ({
  patientId: slice.patientId,
  encounterId: slice.encounterId,
});

/** หน้าเปลี่ยนไปเปิดผู้ป่วย/encounter อื่นระหว่างรอ API ห้ามเขียนผลลงหน้าปัจจุบัน */
const isStaleTarget = (controller: Controller, target: Target): boolean => {
  const current = targetOf(sliceOf(controller));
  return current.patientId !== target.patientId || current.encounterId !== target.encounterId;
};

/** LOAD ล่าสุดเท่านั้นที่เขียนผลได้ */
const loadTokens = new WeakMap<object, number>();

/** อ่าน Principal Diagnosis ของ encounter ถ้าล้มแสดงเฉพาะส่วนนี้ ไม่ทำให้รายการเอกสารหาย */
const loadDiagnosis = async (
  controller: Controller,
  encounterId: number | undefined,
  knownEmrId: number | undefined,
): Promise<{ emrId?: number; diagnosisText: string; diagnosisStatus: DiagnosisStatus }> => {
  const failed = { emrId: knownEmrId, diagnosisText: "", diagnosisStatus: "failed" as const };
  try {
    let emrId = knownEmrId;
    if (emrId === undefined && encounterId !== undefined) {
      const emrResult = await EncounterMedicalRecordList.list({
        pk: encounterId,
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      });
      if (errorMessageOf(emrResult as ApiTuple)) return failed;
      const rows = (emrResult as any)?.[0]?.items ?? (emrResult as any)?.[0];
      emrId = asNumber(Array.isArray(rows) ? rows[0]?.id : undefined);
    }
    if (emrId === undefined) return { diagnosisText: "", diagnosisStatus: "none" };

    const result = await DiagnosisMedicalRecordDetail.retrieve({
      pk: emrId,
      params: {},
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    });
    if (errorMessageOf(result as ApiTuple)) return { ...failed, emrId };

    const diagnosisText = diagnosisTextFromApi((result as any)?.[0]);
    return { emrId, diagnosisText, diagnosisStatus: diagnosisText ? "ready" : "none" };
  } catch {
    return failed;
  }
};

/** ประเภท Encounter ใช้ตัดสินว่าออกใบรับรองจากหน้านี้ได้ไหม ถ้าอ่านไม่ได้จะออกไม่ได้และแจ้งบนหน้าจอ */
const loadEncounterType = async (
  controller: Controller,
  encounterId: number | undefined,
): Promise<string | undefined> => {
  if (encounterId === undefined) return undefined;
  try {
    const result = await EncounterDetail.retrieve({
      pk: encounterId,
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    });
    if (errorMessageOf(result as ApiTuple)) return undefined;
    const type = (result as any)?.[0]?.type;
    return typeof type === "string" && type ? type : undefined;
  } catch {
    return undefined;
  }
};

/* ============================================================== */
/*   SECTION 2 · ACTION HANDLERS                                   */
/* ============================================================== */

/**
 * LOAD: โหลดแบบฟอร์มคลินิก เอกสารสแกน ประวัติใบรับรองแพทย์ และวินิจฉัยของ encounter
 * คืน true เมื่อโหลดสำเร็จและผลยังเป็นของหน้าที่เปิดอยู่
 */
const handleLoad = async (controller: Controller, params: DocumentRunParams): Promise<boolean> => {
  const meta: SliceMeta = { card: params.card, action: ACTIONS.LOAD };

  const token = (loadTokens.get(controller) ?? 0) + 1;
  loadTokens.set(controller, token);

  const prev = sliceOf(controller);
  const requestedPatient = asNumber(params.patientId);
  const requestedEncounter = asNumber(params.encounterId);
  /* ค่าเดิมใน slice ใช้ได้เฉพาะเมื่อยังเป็นผู้ป่วยและ encounter เดิม */
  const sameTarget =
    (requestedPatient === undefined || requestedPatient === prev.patientId) &&
    (requestedEncounter === undefined || requestedEncounter === prev.encounterId);

  const patientHn = String(params.patientHn || (sameTarget ? prev.patientHn : "") || "");
  const patientId = requestedPatient ?? (sameTarget ? prev.patientId : undefined);
  const encounterId = requestedEncounter ?? (sameTarget ? prev.encounterId : undefined);
  const doctorId = asNumber(params.doctorId) ?? (sameTarget ? prev.doctorId : undefined);
  const knownEmrId = asNumber(params.emrId) ?? (sameTarget ? prev.emrId : undefined);

  writeSlice(controller, meta, {
    patientHn,
    patientId,
    encounterId,
    doctorId,
    emrId: knownEmrId,
    error: null,
    ...(sameTarget
      ? {}
      : {
          forms: [],
          documentTypes: [],
          documentCategories: [],
          scannedDocs: [],
          medicalCerts: [],
          medCertTypes: [],
          certDetail: null,
          encounterType: undefined,
          diagnosisText: "",
          diagnosisStatus: "idle" as const,
          actionResult: null,
        }),
  });
  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const target: Target = { patientId, encounterId };
  const isStale = (): boolean =>
    loadTokens.get(controller) !== token || isStaleTarget(controller, target);

  try {
    const extra = extraOf(controller);
    const formsCall = DocumentTypeList.list({
      apiToken: controller.apiToken,
      params: { printable: true, division: "current", disable_pagination: true },
      extra,
    });

    const typesCall = DocumentTypeList.list({
      apiToken: controller.apiToken,
      params: { disable_pagination: true },
      extra,
    });

    const categoriesCall = DocumentCategoryList.list({
      apiToken: controller.apiToken,
      params: { disable_pagination: true },
      extra,
    });

    /* BE เก็บแถวละหน้า ถ้าแบ่งหน้าฝั่ง server เอกสารชุดเดียวจะถูกตัดคนละหน้า */
    const docsCall = patientHn
      ? ScannedDocumentList.list({
          apiToken: controller.apiToken,
          params: { patient__hn: patientHn, disable_pagination: true },
          extra,
        })
      : Promise.resolve([null, null, null]);

    const certTypesCall = DoctorCertificatesTypeList.list({
      apiToken: controller.apiToken,
      extra,
    });

    /* ต้องมี patientId เสมอ: filter ที่เป็น undefined ทำให้ BE คืนใบรับรองของผู้ป่วยทุกคน */
    const certsCall = patientId
      ? FormDataActionLogAllList.list({
          apiToken: controller.apiToken,
          params: { limit: 9999, patient: patientId },
          extra,
        })
      : Promise.resolve([null, null, null]);

    const [formsRes, typesRes, categoriesRes, docsRes, certTypesRes, certsRes, diagnosis, encounterType] = await Promise.all([
      formsCall,
      typesCall,
      categoriesCall,
      docsCall,
      certTypesCall,
      certsCall,
      loadDiagnosis(controller, encounterId, knownEmrId),
      loadEncounterType(controller, encounterId),
    ]);

    if (isStale()) return false;

    /* ส่วนที่ล้มแสดง error เฉพาะส่วนนั้น ส่วนที่โหลดได้ยังใช้งานต่อได้ */
    const errors: string[] = [];
    const sectionOf = (label: string, res: unknown): any => {
      const message = errorMessageOf(res as ApiTuple);
      if (message) {
        errors.push(`โหลด${label}ไม่สำเร็จ: ${message}`);
        return undefined;
      }
      return (res as any)?.[0]?.items ?? (res as any)?.[0];
    };

    const formsRaw = sectionOf("แบบฟอร์ม", formsRes);
    const typesRaw = sectionOf("ประเภทเอกสาร", typesRes);
    const categoriesRaw = sectionOf("หมวดเอกสาร", categoriesRes);
    const docsRaw = sectionOf("เอกสารสแกน", docsRes);
    const certTypesRaw = sectionOf("ชนิดใบรับรองแพทย์", certTypesRes);
    const certsRaw = sectionOf("ใบรับรองแพทย์", certsRes);
    /* ประวัติกรองด้วยรหัสจาก master จึงเขียนได้เมื่อโหลดสำเร็จทั้งคู่ */
    const medCertTypes = certTypesRaw === undefined ? undefined : medCertTypesFromApi(certTypesRaw);

    writeSlice(controller, meta, {
      ...(formsRaw === undefined ? {} : { forms: formsFromApi(formsRaw) }),
      ...(typesRaw === undefined ? {} : { documentTypes: documentTypesFromApi(typesRaw) }),
      ...(categoriesRaw === undefined
        ? {}
        : { documentCategories: documentCategoriesFromApi(categoriesRaw) }),
      ...(docsRaw === undefined
        ? {}
        : { scannedDocs: Array.isArray(docsRaw) ? scannedDocsFromApi(docsRaw) : [] }),
      ...(medCertTypes === undefined ? {} : { medCertTypes }),
      ...(certsRaw === undefined || medCertTypes === undefined
        ? {}
        : {
            medicalCerts: medCertsFromLogs(
              certsRaw,
              medCertTypes.map((type) => type.code),
            ),
          }),
      emrId: diagnosis.emrId,
      encounterType,
      diagnosisText: diagnosis.diagnosisText,
      diagnosisStatus: diagnosis.diagnosisStatus,
      error: errors.length > 0 ? errors.join(" · ") : null,
    });
    if (errors.length > 0) return false;

    setBtn(controller, [meta], BLC_STATUS.SUCCESS);
    return true;
  } catch (err: unknown) {
    if (isStale()) return false;
    const errorMsg = errorTextOf(err) ?? "ไม่สามารถดึงข้อมูลเอกสารได้";
    reportError(controller, meta, errorMsg);
    return false;
  }
};

/**
 * SAVE_SCANNED_DOC: บันทึกเอกสารสแกน 1 ชุด
 * BE เก็บแถวละหน้าและไม่มี API บันทึกรวม จึงส่งทีละหน้าเรียงตามลำดับ
 * หน้าที่ล้มรายงานกลับเพื่อให้ส่งซ้ำเข้าชุดเดิมได้โดยไม่ซ้ำหน้าที่สำเร็จแล้ว
 */
const handleSaveScannedDoc = async (
  controller: Controller,
  params: DocumentRunParams,
): Promise<void> => {
  const meta: SliceMeta = { card: params.card, action: ACTIONS.SAVE_SCANNED_DOC };
  beginAction(controller, meta);

  const prev = sliceOf(controller);
  const target = targetOf(prev);
  const patientId = asNumber(params.patientId ?? prev.patientId);
  const encounterId = asNumber(params.encounterId ?? prev.encounterId);
  const docName = String(params.docName || "เอกสารสแกน");
  const typeId = asNumber(params.typeId);
  const pages = Array.isArray(params.pages) ? (params.pages as ScanPage[]) : [];
  const documentCode = String(params.documentCode || newDocumentCode());

  if (!patientId) {
    failAction(controller, meta, params, "ยังไม่มีข้อมูลผู้ป่วยสำหรับการบันทึกเอกสารสแกน");
    return;
  }
  if (typeId === undefined) {
    failAction(controller, meta, params, "กรุณาเลือกประเภทเอกสาร");
    return;
  }
  if (pages.length === 0) {
    failAction(controller, meta, params, "ยังไม่ได้เลือกไฟล์เอกสาร");
    return;
  }

  const failedPages: number[] = [];
  let firstError: string | null = null;

  for (let index = 0; index < pages.length; index += 1) {
    const pageNo = pages[index].pageNo ?? index + 1;
    let error: string | null;
    try {
      const res = await ScannedDocumentList.create({
        apiToken: controller.apiToken,
        data: scannedDocPagePayload({
          patientId,
          encounterId,
          docName,
          typeId,
          documentCode,
          ownerDivisionId: asNumber(controller.data?.division),
          image: pages[index].image,
          page: pageNo,
        }),
        extra: extraOf(controller),
      });
      error = errorMessageOf(res as ApiTuple);
    } catch (err: unknown) {
      error = errorTextOf(err) ?? "บันทึกเอกสารสแกนไม่สำเร็จ";
    }
    if (error) {
      failedPages.push(pageNo);
      firstError = firstError ?? error;
    }
  }

  if (isStaleTarget(controller, target)) {
    setBtn(controller, [meta], failedPages.length > 0 ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS);
    return;
  }

  const savedCount = pages.length - failedPages.length;
  const reloaded =
    savedCount > 0 &&
    (await handleLoad(controller, {
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD,
      card: params.card,
      patientHn: prev.patientHn,
      patientId,
      encounterId,
    }));

  if (isStaleTarget(controller, target)) {
    setBtn(controller, [meta], failedPages.length > 0 ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS);
    return;
  }

  if (failedPages.length > 0) {
    writeActionResult(
      controller,
      meta,
      params,
      { ok: false, label: docName, documentCode, failedPages },
      `บันทึกสำเร็จ ${savedCount} จาก ${pages.length} หน้า หน้าที่ไม่สำเร็จ: ${failedPages.join(", ")} · ${firstError}`,
    );
    return;
  }

  writeActionResult(controller, meta, params, {
    ok: true,
    label: docName,
    reloadFailed: !reloaded,
  });
  setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/**
 * DELETE_SCANNED_DOC: ยกเลิกเอกสารสแกนทั้งชุด
 * BE ตรวจ username/password และสิทธิต่อแถว จึงส่งทีละหน้าและหยุดทันทีที่ล้ม
 * เพื่อไม่ยิงรหัสผ่านผิดซ้ำตามจำนวนหน้า
 */
const handleDeleteScannedDoc = async (
  controller: Controller,
  params: DocumentRunParams,
): Promise<void> => {
  const meta: SliceMeta = { card: params.card, action: ACTIONS.DELETE_SCANNED_DOC };
  beginAction(controller, meta);

  const prev = sliceOf(controller);
  const target = targetOf(prev);
  const docId = asNumber(params.docId);
  const rowIds = Array.isArray(params.rowIds) ? (params.rowIds as number[]) : [];
  const username = String(params.username ?? "");
  const password = String(params.password ?? "");
  const cancelNote = String(params.cancelNote ?? "").trim();

  if (docId === undefined || rowIds.length === 0) {
    failAction(controller, meta, params, "ไม่พบเอกสารที่ต้องการยกเลิก");
    return;
  }
  if (!username || !password) {
    failAction(controller, meta, params, "กรุณาระบุชื่อผู้ใช้และรหัสผ่าน");
    return;
  }
  if (!cancelNote) {
    failAction(controller, meta, params, "กรุณาระบุเหตุผลในการยกเลิก");
    return;
  }

  let cancelledCount = 0;
  let error: string | null = null;

  for (const rowId of rowIds) {
    try {
      const res = await ScannedDocumentDetail.patch({
        pk: rowId,
        params: {},
        apiToken: controller.apiToken,
        data: { username, password, active: false, cancel_note: cancelNote },
        extra: extraOf(controller),
      });
      error = errorMessageOf(res as ApiTuple);
    } catch (err: unknown) {
      error = errorTextOf(err) ?? "ยกเลิกเอกสารสแกนไม่สำเร็จ";
    }
    if (error) break;
    cancelledCount += 1;
  }

  if (isStaleTarget(controller, target)) {
    setBtn(controller, [meta], error ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS);
    return;
  }

  if (error) {
    /* ยกเลิกไปแล้วบางหน้า ต้องอ่านรายการใหม่เพื่อให้เห็นสถานะจริง */
    if (cancelledCount > 0) {
      await handleLoad(controller, {
        sequence: SEQUENCE_NAME,
        action: ACTIONS.LOAD,
        card: params.card,
        patientHn: prev.patientHn,
        patientId: prev.patientId,
        encounterId: prev.encounterId,
      });
      if (isStaleTarget(controller, target)) {
        setBtn(controller, [meta], BLC_STATUS.ERROR);
        return;
      }
      error = `ยกเลิกสำเร็จ ${cancelledCount} จาก ${rowIds.length} หน้า · ${error}`;
    }
    failAction(controller, meta, params, error);
    return;
  }

  writeSlice(controller, meta, {
    scannedDocs: sliceOf(controller).scannedDocs.filter((doc) => doc.id !== docId),
  });
  writeActionResult(
    controller,
    meta,
    params,
    { ok: true, label: String(params.label ?? "") },
    null,
  );
};

/** แพทย์และอาการสำคัญที่ต้องพิมพ์บนใบรับรอง อ่านตอนออกใบเพื่อให้เป็นค่าล่าสุด */
const loadCertContext = async (
  controller: Controller,
  doctorId: number,
  emrId: number,
): Promise<
  | { error: string }
  | { doctor: ReturnType<typeof doctorForCertFromApi>; chiefComplaint: string }
> => {
  const extra = extraOf(controller);
  const [doctorRes, emrRes] = await Promise.all([
    DoctorDetail.retrieve({ pk: doctorId, apiToken: controller.apiToken, extra }),
    PatientEMRList.get({
      emr: emrId,
      params: { auto_checkin: false },
      apiToken: controller.apiToken,
      extra,
    }),
  ]);

  const doctorError = errorMessageOf(doctorRes as ApiTuple);
  if (doctorError) return { error: `อ่านข้อมูลแพทย์ไม่สำเร็จ: ${doctorError}` };
  const emrError = errorMessageOf(emrRes as ApiTuple);
  if (emrError) return { error: `อ่านเวชระเบียนไม่สำเร็จ: ${emrError}` };

  const doctor = doctorForCertFromApi((doctorRes as any)?.[0]);
  if (!doctor.name) return { error: "ไม่พบชื่อแพทย์สำหรับออกใบรับรอง" };

  return {
    doctor,
    chiefComplaint: String((emrRes as any)?.[0]?.patient_illness?.chief_complaint ?? ""),
  };
};

/**
 * ISSUE_MED_CERT: ออกใบรับรองแพทย์ใหม่ด้วยโครงข้อมูลเดียวกับ V3
 * เลขที่ใช้ id ที่ BE สร้าง ห้ามคำนวณฝั่ง client เพราะซ้ำกันข้ามผู้ป่วยได้
 */
const handleIssueMedCert = async (
  controller: Controller,
  params: DocumentRunParams,
): Promise<void> => {
  const meta: SliceMeta = { card: params.card, action: ACTIONS.ISSUE_MED_CERT };
  beginAction(controller, meta);

  const prev = sliceOf(controller);
  const target = targetOf(prev);
  const encounterId = asNumber(params.encounterId ?? prev.encounterId);
  const doctorId = asNumber(params.doctorId ?? prev.doctorId);
  const emrId = prev.emrId;
  const type = prev.medCertTypes.find((item) => item.code === params.typeCode);
  const patient = (params.patient ?? {}) as Record<string, any>;
  const dx = prev.diagnosisText;
  const rest = params.rest === true;
  const days = Number(params.days ?? 0);

  if (!encounterId) {
    failAction(controller, meta, params, "ยังไม่มีข้อมูล Encounter สำหรับการออกใบรับรองแพทย์");
    return;
  }
  if (!doctorId || !emrId) {
    failAction(controller, meta, params, "ยังไม่มีแพทย์หรือเวชระเบียนของ Encounter นี้");
    return;
  }
  if (type === undefined) {
    failAction(controller, meta, params, "กรุณาเลือกชนิดใบรับรองแพทย์");
    return;
  }
  if (!patient.full_name || !dx) {
    failAction(controller, meta, params, "ข้อมูลผู้ป่วยหรือการวินิจฉัยไม่ครบ ยังออกใบรับรองแพทย์ไม่ได้");
    return;
  }

  try {
    const context = await loadCertContext(controller, doctorId, emrId);
    if (isStaleTarget(controller, target)) {
      setBtn(controller, [meta], BLC_STATUS.ERROR);
      return;
    }
    if ("error" in context) {
      failAction(controller, meta, params, context.error);
      return;
    }

    const data = examAbsenceCertData({
      formCode: type.code,
      patient,
      encounter: {
        id: encounterId,
        number: String(params.encounterNumber ?? ""),
        type: String(params.encounterType ?? ""),
      },
      doctor: context.doctor,
      chiefComplaint: context.chiefComplaint,
      diagnosis: dx,
      comment: String(params.comment ?? ""),
      rest,
      fromDate: String(params.fromDate ?? ""),
      days,
    });

    const res = await FormDataList.create({
      apiToken: controller.apiToken,
      data: medCertCreatePayload({ encounterId, doctorId, type, data }),
      extra: extraOf(controller),
    });

    if (isStaleTarget(controller, target)) {
      setBtn(controller, [meta], BLC_STATUS.SUCCESS);
      return;
    }

    const err = errorMessageOf(res as ApiTuple);
    if (err) {
      failAction(controller, meta, params, err);
      return;
    }

    const issuedId = asNumber((res as any)?.[0]?.id);
    const reloaded = await handleLoad(controller, {
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD,
      card: params.card,
      patientHn: prev.patientHn,
      patientId: prev.patientId,
      encounterId,
      doctorId,
    });

    if (!isStaleTarget(controller, target)) {
      writeSlice(controller, meta, {
        certDetail:
          issuedId === undefined
            ? null
            : {
                id: issuedId,
                formCode: type.code,
                formName: type.name,
                encounterId,
                doctorId,
                data,
              },
      });
      writeActionResult(controller, meta, params, {
        ok: true,
        label: issuedId === undefined ? "" : String(issuedId),
        reloadFailed: !reloaded,
      });
    }
    setBtn(controller, [meta], BLC_STATUS.SUCCESS);
  } catch (err: unknown) {
    if (isStaleTarget(controller, target)) {
      setBtn(controller, [meta], BLC_STATUS.ERROR);
      return;
    }
    failAction(controller, meta, params, errorTextOf(err) ?? "ออกใบรับรองแพทย์ไม่สำเร็จ");
  }
};

/** GET_MED_CERT: อ่านเนื้อหาเต็มของใบรับรองเมื่อเปิดดู */
const handleGetMedCert = async (
  controller: Controller,
  params: DocumentRunParams,
): Promise<void> => {
  const meta: SliceMeta = { card: params.card, action: ACTIONS.GET_MED_CERT };
  beginAction(controller, meta);
  writeSlice(controller, meta, { certDetail: null });
  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const target = targetOf(sliceOf(controller));
  const certId = asNumber(params.certId);
  if (certId === undefined) {
    reportError(controller, meta, "ไม่พบใบรับรองแพทย์ที่ต้องการเปิด");
    return;
  }

  try {
    const res = await FormDataDetail.retrieve({
      pk: certId,
      params: {},
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    });
    if (isStaleTarget(controller, target)) {
      setBtn(controller, [meta], BLC_STATUS.ERROR);
      return;
    }

    const err = errorMessageOf(res as ApiTuple);
    if (err) {
      reportError(controller, meta, err);
      return;
    }

    const row = (res as any)?.[0] ?? {};
    const certDetail: MedCertDetail = {
      id: certId,
      formCode: String(params.formCode ?? ""),
      formName: String(params.formName ?? ""),
      encounterId: asNumber(row.encounter),
      doctorId: asNumber(row.doctor),
      data: row.data ?? {},
    };
    writeSlice(controller, meta, { certDetail, error: null });
  } catch (err: unknown) {
    if (isStaleTarget(controller, target)) {
      setBtn(controller, [meta], BLC_STATUS.ERROR);
      return;
    }
    reportError(controller, meta, errorTextOf(err) ?? "เปิดใบรับรองแพทย์ไม่สำเร็จ");
  }
};

/** REPRINT_MED_CERT: นับจำนวนพิมพ์เพิ่มก่อน แล้ว card จึงสั่งพิมพ์ตามผลนี้ */
const handleReprintMedCert = async (
  controller: Controller,
  params: DocumentRunParams,
): Promise<void> => {
  const meta: SliceMeta = { card: params.card, action: ACTIONS.REPRINT_MED_CERT };
  beginAction(controller, meta);

  const prev = sliceOf(controller);
  const target = targetOf(prev);
  const detail = prev.certDetail;
  if (detail === null) {
    failAction(controller, meta, params, "ไม่พบใบรับรองแพทย์ที่ต้องการพิมพ์");
    return;
  }

  try {
    const payload = medCertReprintPayload(detail);
    const res = await FormDataDetail.update({
      pk: detail.id,
      data: payload,
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    });

    if (isStaleTarget(controller, target)) {
      setBtn(controller, [meta], BLC_STATUS.SUCCESS);
      return;
    }

    const err = errorMessageOf(res as ApiTuple);
    if (err) {
      failAction(controller, meta, params, err);
      return;
    }

    writeSlice(controller, meta, { certDetail: { ...detail, data: payload.data } });
    writeActionResult(controller, meta, params, { ok: true, label: String(detail.id) }, null);
  } catch (err: unknown) {
    if (isStaleTarget(controller, target)) {
      setBtn(controller, [meta], BLC_STATUS.ERROR);
      return;
    }
    failAction(controller, meta, params, errorTextOf(err) ?? "พิมพ์ใบรับรองแพทย์ไม่สำเร็จ");
  }
};

/* ============================================================== */
/*   SECTION 3 · SEQUENCE ENTRY POINTS                            */
/* ============================================================== */

export const Action = async (
  controller: Controller,
  params: DocumentRunParams,
): Promise<void> => {
  const action = params.action;

  if (action === ACTIONS.LOAD) {
    await handleLoad(controller, params);
  } else if (action === ACTIONS.SAVE_SCANNED_DOC) {
    await handleSaveScannedDoc(controller, params);
  } else if (action === ACTIONS.DELETE_SCANNED_DOC) {
    await handleDeleteScannedDoc(controller, params);
  } else if (action === ACTIONS.ISSUE_MED_CERT) {
    await handleIssueMedCert(controller, params);
  } else if (action === ACTIONS.GET_MED_CERT) {
    await handleGetMedCert(controller, params);
  } else if (action === ACTIONS.REPRINT_MED_CERT) {
    await handleReprintMedCert(controller, params);
  }
};

export const Start = async (
  controller: Controller,
  params: DocumentRunParams,
): Promise<void> => {
  const current = controller.getState()[DOCUMENT_SLICE_KEY];
  if (!current || params.restart) {
    controller.setState({
      [DOCUMENT_SLICE_KEY]: {
        ...emptyDocumentV4Slice(),
        patientHn: params.patientHn ?? "",
        encounterId: asNumber(params.encounterId),
        patientId: asNumber(params.patientId),
        doctorId: asNumber(params.doctorId),
        emrId: asNumber(params.emrId),
        sequenceIndex: "Action",
      },
    });
  }
  await Action(controller, params);
};
